import User from '@/models/User';
import Segment from '@/utils/segment';
import { DashboardService } from '@/modules/dashboard/services';
import Organisation from '@/models/Organisation';

export default class Auth {
  static setToken(token: string): void {
    if (token) {
      Segment.reInitAnalytics(process.env.VUE_APP_SEGMENT_ID);
    }
    window.localStorage.setItem('token', token);
  }

  static getToken(): string | null {
    return window.localStorage.getItem('token');
  }

  static setRefreshToken(token?: string): void {
    if (token) {
      window.localStorage.setItem('refreshToken', token);
    }
  }

  static getRefreshToken(): string | null {
    return window.localStorage.getItem('refreshToken');
  }

  static destroyToken(): void {
    window.localStorage.removeItem('token');
    window.localStorage.removeItem('userId');
    window.localStorage.removeItem('userIsImpersonated');
    window.localStorage.removeItem('refreshToken');
    Segment.resetAnalytics();
  }

  static setUserId(userId: string, user?: User): void {
    // If user is impersonated, remove analytics
    if (this.userIsImpersonated()) {
      Segment.removeAnalytics();
    }

    Segment.setIdentificationToken(userId, {
      roles: user?.roles,
      organization_types: user?.organization_types,
      organizations: user?.organizations?.data?.map(
        (organization: Organisation) => organization.name
      ),
      organization_ids: user?.organizations?.data?.map(
        (organization: Organisation) => organization.id
      )
    });
    window.localStorage.setItem('userId', userId);
  }

  static getUserId(): string | null {
    return window.localStorage.getItem('userId');
  }

  static setUser(user: User): void {
    window.localStorage.setItem('user', JSON.stringify(user));
  }

  static getUser(): User | null {
    const user = window.localStorage.getItem('user');
    if (!user) {
      return null;
    }
    return JSON.parse(user);
  }

  static removeUser(): void {
    window.localStorage.removeItem('user');
  }

  static isAuthenticated(): boolean {
    const userIsAuthenticated =
      Auth.getUserId() != null && Auth.getToken() != null;
    if (userIsAuthenticated && this.userIsImpersonated()) {
      Segment.removeAnalytics();
    }
    return userIsAuthenticated;
  }

  static setActiveRoles(roles: any) {
    const user: User | null = Auth.getUser();
    if (user) {
      user.roles = roles;
      Auth.setUser(user);
    }
  }

  static setUserIsImpersonated(value: string) {
    if (value === 'y') {
      Segment.removeAnalytics();
    }
    window.localStorage.setItem('userIsImpersonated', value);
  }

  static userIsImpersonated(): boolean | null {
    return window.localStorage.getItem('userIsImpersonated') === 'y';
  }

  static async setTokenAndUserDetails(token: string, refreshToken?: string) {
    this.setToken(token);
    const user = JSON.parse(atob(token.split('.')[1]));
    this.setUserId(user.id);
    const userDetails = await DashboardService.fetchCurrentUser();
    this.setUserId(user.id, userDetails);
    this.setUserIsImpersonated('n');
    this.setRefreshToken(refreshToken);
  }
}
